import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox as MUICheckbox,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { capitalize } from 'lodash';
import React from 'react';

import { IColumnItem } from '../../../types/tables';

interface IProps {
  anchorEl: HTMLElement | null;
  position: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  columns: IColumnItem[];
  visibleColumns: Record<string, boolean>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onColumnVisibilityChange: (columnId: string) => void;
  onToggleAll: (checked: boolean) => void;
  onReset: () => void;
  requiredColumnIds: string[];
  areAllColumnsVisible: boolean;
}

const ColumnsVisibilityPopover: React.FC<IProps> = ({
  anchorEl,
  position,
  open,
  onClose,
  columns,
  visibleColumns,
  searchQuery,
  onSearchChange,
  onColumnVisibilityChange,
  onToggleAll,
  onReset,
  requiredColumnIds,
  areAllColumnsVisible,
}) => {
  const filteredColumns = React.useMemo(() => {
    if (!searchQuery) {
      return columns;
    }

    return columns.filter((column) => {
      const label = typeof column.label === 'string' ? column.label : column.id;
      return label.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [columns, searchQuery]);

  const isIndeterminate = !areAllColumnsVisible && Object.values(visibleColumns).some((v) => v);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      keepMounted
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorPosition={position || undefined}
      anchorReference={position ? 'anchorPosition' : 'anchorEl'}
    >
      <Box sx={{ px: 0, py: 1.5, minWidth: 250 }}>
        <Typography variant={'subtitle2'} sx={{ mb: 1, px: 1.5 }}>
          Select Visible Columns
        </Typography>
        <Box sx={{ px: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
          <FormGroup sx={{ display: 'flex', flexDirection: 'column' }}>
            {filteredColumns.map((column) => (
              <MenuItem
                sx={{
                  p: 0,
                  '& .MuiFormControlLabel-root': {
                    width: '100%',
                    marginRight: 0,
                    paddingLeft: 0.5,
                  },
                }}
                key={column.id}
                selected={visibleColumns[column.id]}
              >
                <FormControlLabel
                  key={column.id}
                  sx={{
                    ml: 0,
                  }}
                  control={
                    <MUICheckbox
                      sx={{ px: 1.5, py: 0.5, mr: 0.5 }}
                      checked={visibleColumns[column.id]}
                      onChange={() => onColumnVisibilityChange(column.id)}
                      disabled={requiredColumnIds.includes(column.id)}
                    />
                  }
                  label={capitalize(typeof column.label === 'string' ? column.label : column.id)}
                />
              </MenuItem>
            ))}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, px: 2 }}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={
              <MUICheckbox
                sx={{ px: 0, py: 0.5, mr: 2 }}
                checked={areAllColumnsVisible}
                indeterminate={isIndeterminate}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            }
            label="Show/Hide All"
          />

          <Button size="small" onClick={onReset} sx={{ fontSize: 14, marginLeft: 2 }}>
            Reset
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default ColumnsVisibilityPopover;
